import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Edit3, Plus, Image, X, Check, GripVertical, Package, Loader2, Star } from 'lucide-react';
import api from '../api';
import Toast from './Toast';

const ProductImagesTab = ({ editingAgent }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    keywords: '',
    file: null,
    preview: null,
    hide_description: false,
    hide_name: false,
  });

  useEffect(() => {
    if (editingAgent?.id) {
      loadImages();
    }
  }, [editingAgent?.id]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/agents/${editingAgent.id}/product-images`);
      setImages(response.data);
    } catch (err) {
      console.error('Failed to load product images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setToast({ message: 'Please select a valid image file (JPG, PNG, GIF, WebP, SVG)', type: 'error' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setToast({ message: 'File too large. Maximum size is 10MB', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadForm((prev) => ({ ...prev, file, preview: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title.trim() || !uploadForm.description.trim()) {
      setToast({ message: 'Please fill in title, description, and select an image', type: 'error' });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title.trim());
      formData.append('description', uploadForm.description.trim());
      formData.append('keywords', uploadForm.keywords.trim());
      formData.append('display_order', images.length);
      formData.append('hide_description', uploadForm.hide_description);
      formData.append('hide_name', uploadForm.hide_name);

      await api.post(`/agents/${editingAgent.id}/product-images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setToast({ message: 'Product image uploaded successfully!', type: 'success' });
      setShowUploadForm(false);
      setUploadForm({ title: '', description: '', keywords: '', file: null, preview: null, hide_description: false, hide_name: false });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadImages();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Failed to upload image', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSetFeatured = async (imageId) => {
    try {
      await api.put(`/agents/${editingAgent.id}/product-images/${imageId}`, { is_featured: true });
      setToast({ message: 'Featured image updated!', type: 'success' });
      await loadImages();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Failed to set featured', type: 'error' });
    }
  };

  const handleUpdate = async (imageId) => {
    if (!editingImage) return;
    try {
      await api.put(`/agents/${editingAgent.id}/product-images/${imageId}`, {
        title: editingImage.title,
        description: editingImage.description,
        keywords: editingImage.keywords,
        hide_description: editingImage.hide_description,
        hide_name: editingImage.hide_name,
      });
      setToast({ message: 'Product image updated!', type: 'success' });
      setEditingImage(null);
      await loadImages();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Failed to update', type: 'error' });
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this product image?')) return;
    try {
      await api.delete(`/agents/${editingAgent.id}/product-images/${imageId}`);
      setToast({ message: 'Product image deleted', type: 'success' });
      await loadImages();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Failed to delete', type: 'error' });
    }
  };

  const parseKeywords = (kw) => {
    if (!kw) return [];
    try {
      return JSON.parse(kw);
    } catch {
      return kw.split(',').map((k) => k.trim()).filter(Boolean);
    }
  };

  if (!editingAgent) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Save agent first to manage product images</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          Product Images
        </h4>
        <p className="text-sm text-muted-foreground mb-2">
          Upload product images with descriptions. The AI agent will show relevant images during conversations based on context.
        </p>
        <p className="text-xs text-muted-foreground mb-2">
          Use the <strong>LiveAvatar with Products</strong> embed to display the avatar alongside product images on your website.
        </p>
        <p className="text-xs text-muted-foreground">
          Set a <strong>featured image</strong> to display before the call starts. It will be replaced by conversation-matched products once the call begins.
        </p>
      </div>

      {/* Upload Button */}
      {!showUploadForm && (
        <button
          type="button"
          onClick={() => setShowUploadForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Product Image
        </button>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-muted/20 border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-foreground">Upload New Product Image</h5>
            <button
              type="button"
              onClick={() => {
                setShowUploadForm(false);
                setUploadForm({ title: '', description: '', keywords: '', file: null, preview: null, hide_description: false, hide_name: false });
              }}
              className="p-1 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Image Preview & File Input */}
          <div className="flex gap-4">
            <div
              className="w-32 h-32 border-2 border-dashed border-border rounded-xl flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors overflow-hidden flex-shrink-0"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadForm.preview ? (
                <img src={uploadForm.preview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Click to upload</span>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

            <div className="flex-1 space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Product Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm((p) => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g. Premium Wireless Headphones"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Description *</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={2}
                  placeholder="Describe the product. AI will use this to decide when to show the image."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Keywords <span className="text-muted-foreground font-normal">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.keywords}
                  onChange={(e) => setUploadForm((p) => ({ ...p, keywords: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="headphones, audio, wireless, music"
                />
              </div>
              <div className="flex gap-4 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadForm.hide_name}
                    onChange={(e) => setUploadForm((p) => ({ ...p, hide_name: e.target.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-xs text-muted-foreground">Hide name</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadForm.hide_description}
                    onChange={(e) => setUploadForm((p) => ({ ...p, hide_description: e.target.checked }))}
                    className="rounded border-border"
                  />
                  <span className="text-xs text-muted-foreground">Hide description</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowUploadForm(false);
                setUploadForm({ title: '', description: '', keywords: '', file: null, preview: null, hide_description: false, hide_name: false });
              }}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !uploadForm.file || !uploadForm.title.trim() || !uploadForm.description.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Images List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <Image className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground">No product images uploaded yet</p>
          <p className="text-xs text-muted-foreground mt-1">Upload images to display during LiveAvatar conversations</p>
        </div>
      ) : (
        <div className="space-y-3">
          {images.map((img) => (
            <div key={img.id} className={`flex gap-4 p-4 bg-muted/20 border rounded-xl hover:border-primary/30 transition-colors ${img.is_featured ? 'border-amber-500/50' : 'border-border'}`}>
              {/* Thumbnail */}
              <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                <img src={img.image_url} alt={img.title} className="w-full h-full object-cover" />
                {img.is_featured && (
                  <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-amber-500 text-amber-950 text-[10px] font-semibold rounded">
                    Featured
                  </div>
                )}
              </div>

              {/* Details */}
              {editingImage && editingImage.id === img.id ? (
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={editingImage.title}
                    onChange={(e) => setEditingImage({ ...editingImage, title: e.target.value })}
                    className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <textarea
                    value={editingImage.description}
                    onChange={(e) => setEditingImage({ ...editingImage, description: e.target.value })}
                    className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    rows={2}
                  />
                  <input
                    type="text"
                    value={editingImage.keywords || ''}
                    onChange={(e) => setEditingImage({ ...editingImage, keywords: e.target.value })}
                    className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Keywords (comma-separated)"
                  />
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingImage.hide_name ?? false}
                        onChange={(e) => setEditingImage({ ...editingImage, hide_name: e.target.checked })}
                        className="rounded border-border"
                      />
                      <span className="text-xs text-muted-foreground">Hide name</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingImage.hide_description ?? false}
                        onChange={(e) => setEditingImage({ ...editingImage, hide_description: e.target.checked })}
                        className="rounded border-border"
                      />
                      <span className="text-xs text-muted-foreground">Hide description</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleUpdate(img.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium"
                    >
                      <Check className="w-3 h-3" /> Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingImage(null)}
                      className="px-3 py-1.5 text-muted-foreground hover:text-foreground text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-semibold text-foreground truncate">{img.title}</h5>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{img.description}</p>
                  {(img.hide_name || img.hide_description) && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {img.hide_name && <span className="px-2 py-0.5 bg-muted text-muted-foreground text-[10px] rounded">Hide name</span>}
                      {img.hide_description && <span className="px-2 py-0.5 bg-muted text-muted-foreground text-[10px] rounded">Hide desc</span>}
                    </div>
                  )}
                  {img.keywords && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {parseKeywords(img.keywords).map((kw, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {(!editingImage || editingImage.id !== img.id) && (
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleSetFeatured(img.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      img.is_featured
                        ? 'bg-amber-500/20 text-amber-500'
                        : 'hover:bg-muted text-muted-foreground hover:text-amber-500'
                    }`}
                    title={img.is_featured ? 'Featured image' : 'Set as featured'}
                  >
                    <Star className={`w-3.5 h-3.5 ${img.is_featured ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const kwStr = img.keywords
                        ? (() => { try { return JSON.parse(img.keywords).join(', '); } catch { return img.keywords; } })()
                        : '';
                      setEditingImage({
                        id: img.id,
                        title: img.title,
                        description: img.description,
                        keywords: kwStr,
                        hide_name: img.hide_name ?? false,
                        hide_description: img.hide_description ?? false,
                      });
                    }}
                    className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                    title="Edit"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(img.id)}
                    className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors text-muted-foreground hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ProductImagesTab;
